import type { CardType } from "@mn/shared";
import { create } from "zustand";
import { type Camera, clampCamera } from "./camera";

export type Tool = "select" | "hand" | { place: CardType };

interface BoardUI {
  camera: Camera;
  viewport: { w: number; h: number };
  selection: Set<string>;
  editingId: string | null;
  hoverId: string | null;
  tool: Tool;
  marquee: { x: number; y: number; w: number; h: number } | null;
  /** Alignment guides (page coords) shown while dragging: vertical x lines and horizontal y lines. */
  guides: { v: number[]; h: number[] } | null;
  setGuides: (g: BoardUI["guides"]) => void;
  /** Live positions while dragging (page px), not yet committed to the doc. */
  dragging: Map<string, { x: number; y: number }> | null;
  resizing: { id: string; w: number; h: number | null } | null;
  contextMenu: { sx: number; sy: number; px: number; py: number; cardId: string | null } | null;
  /** Live drop target while dragging over a column. */
  columnDrop: { columnId: string; index: number } | null;
  selectedLine: string | null;
  picker: { kind: "labels" | "reactions" | "alias"; sx: number; sy: number; px?: number; py?: number } | null;
  /** Board tile under the pointer while dragging cards (drop = move to that board). */
  tileDrop: string | null;
  setTileDrop: (b: string | null) => void;
  setPicker: (p: BoardUI["picker"]) => void;
  setCamera: (c: Camera | ((c: Camera) => Camera)) => void;
  setViewport: (w: number, h: number) => void;
  select: (ids: string[] | Set<string>, opts?: { add?: boolean; toggle?: boolean }) => void;
  clearSelection: () => void;
  setEditing: (id: string | null) => void;
  setHover: (id: string | null) => void;
  setTool: (t: Tool) => void;
  setMarquee: (m: BoardUI["marquee"]) => void;
  setDragging: (d: BoardUI["dragging"]) => void;
  setResizing: (r: BoardUI["resizing"]) => void;
  setContextMenu: (m: BoardUI["contextMenu"]) => void;
  setColumnDrop: (d: BoardUI["columnDrop"]) => void;
}

export const useBoardUI = create<BoardUI>((set, get) => ({
  camera: { x: 0, y: 0, z: 1 },
  viewport: { w: 1200, h: 800 },
  selection: new Set(),
  editingId: null,
  hoverId: null,
  tool: "select",
  marquee: null,
  guides: null,
  setGuides: (guides) => set({ guides }),
  dragging: null,
  resizing: null,
  contextMenu: null,
  columnDrop: null,
  selectedLine: null,
  picker: null,
  tileDrop: null,
  setTileDrop: (tileDrop) => set({ tileDrop }),
  setPicker: (picker) => set({ picker }),
  setCamera: (c) => {
    const next = typeof c === "function" ? c(get().camera) : c;
    const { w, h } = get().viewport;
    set({ camera: clampCamera(next, w, h) });
  },
  setViewport: (w, h) => set({ viewport: { w, h } }),
  select: (ids, opts) =>
    set((s) => {
      if (s.selectedLine) s = { ...s, selectedLine: null } as typeof s;
      const list = Array.isArray(ids) ? ids : [...ids];
      if (opts?.toggle) {
        const next = new Set(s.selection);
        for (const id of list) next.has(id) ? next.delete(id) : next.add(id);
        return { selection: next };
      }
      if (opts?.add) return { selection: new Set([...s.selection, ...list]) };
      return { selection: new Set(list) };
    }),
  clearSelection: () => set({ selection: new Set(), editingId: null, selectedLine: null }),
  setEditing: (id) => set({ editingId: id, selection: id ? new Set([id]) : get().selection }),
  setHover: (id) => set({ hoverId: id }),
  setTool: (tool) => set({ tool }),
  setMarquee: (marquee) => set({ marquee }),
  setDragging: (dragging) => set({ dragging }),
  setResizing: (resizing) => set({ resizing }),
  setContextMenu: (contextMenu) => set({ contextMenu }),
  setColumnDrop: (columnDrop) => set({ columnDrop }),
}));

export const useIsSelected = (id: string) => useBoardUI((s) => s.selection.has(id));

if (import.meta.env.DEV) (window as unknown as { __mn: unknown }).__mn = useBoardUI;
